import React from 'react'
import { useWizard } from '@/components/common/wizard/WizardContext'
import { formatCurrencyAutoLocale } from '@/utils/tools'
import { parseBudgetAmount } from '@/utils/audience-builder'
import { hasCampaignBudget } from '@/utils/campaign-budget'
import { MODES, START_CHOICES, START_CHOICE_LABELS, WARMUP_STEP, eventLabel } from '@/utils/pixel-warmup'

interface WizardContextType {
    getStepContentData: (stepName: string) => any
}

interface WarmupReviewProps {
    currency?: string
}

/**
 * The start the user chose for the campaign, on the review step: launching confirms it. Nothing is shown for the
 * usual start without a maximum daily budget.
 */
const WarmupReview: React.FC<WarmupReviewProps> = ({ currency }) => {
    const { getStepContentData } = useWizard() as WizardContextType
    if (hasCampaignBudget(getStepContentData('budgetSchedule'))) return null

    const choice = getStepContentData(WARMUP_STEP) || {}
    const start = choice.startChoice || START_CHOICES.DIRECT
    const maxAmount = parseBudgetAmount(choice.maxDailyBudget)
    if (start === START_CHOICES.DIRECT && maxAmount === null) return null

    const warmupAmount = parseBudgetAmount(choice.warmupBudget)
    const format = (amount: number) => formatCurrencyAutoLocale({ amount, currency })

    return (
        <div
            className="tw:mx-6 tw:mt-6 tw:flex tw:flex-col tw:gap-2 tw:rounded-lg tw:border tw:p-4"
            data-testid="warmup-review"
        >
            <span className="tw:text-sm tw:font-semibold">Start of the campaign</span>
            <p className="tw:text-sm">{(START_CHOICE_LABELS as Record<string, string>)[start]}</p>
            {start === START_CHOICES.WARMUP && (
                <p className="tw:text-sm">
                    {`Warm-up ad set on ${eventLabel(choice.warmupEvent)}${
                        warmupAmount !== null ? `, ${format(warmupAmount)} a day` : ''
                    }. ${
                        choice.mode === MODES.AUTOMATED
                            ? 'Turba expands the campaign by itself when the warm-up has enough events.'
                            : 'Turba proposes the expansion and nothing changes until you confirm it.'
                    } Your audience ad sets stay paused until then.`}
                </p>
            )}
            {start === START_CHOICES.AUTOMATIC && (
                <p className="tw:text-sm">
                    Turba reads your pixel when the campaign is published and decides whether to start with a warm-up.
                </p>
            )}
            {maxAmount !== null && <p className="tw:text-sm">{`Maximum daily budget: ${format(maxAmount)}.`}</p>}
        </div>
    )
}

export default WarmupReview
